<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import { isEqual } from 'lodash-es'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'

    import { Breadcrumb } from 'breadcrumb'

    import type { User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Province from 'wdom/components/contacts/Province.svelte'

    import 'codemirror/mode/markdown/markdown'
    import 'codemirror/addon/edit/trailingspace'

    import Icon from '@iconify/svelte'

    import { asAny, spath, sreplace } from 'hydria/svelte_helpers'

    import CodeMirror from 'admin/components/CodeMirror.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const userId = getContext('userId') as Readable<string>
    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const addresses = sderived(userh.addresses, ($addresses) => $addresses)

    const MARKDOWN_UPDATE_DELAY_MS = 750

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const crumbs = sderived(status, ($status) => [
        { txt: $_('Users'), url: spath('/wdom-users') },
        {
            txt: $status == 'ready' ? $user.email : $status == 'loading' ? '…' : '<Icon inline icon="fa:exclamation-triangle" />',
            html: $status != 'ready' && $status != 'loading',
            url: spath(`/wdom-user/${$userId}`),
        },
        { txt: $_('Envoi d’un courriel') },
    ])

    let working = $state(false)
    let form = $state({
        email: null,
        subject: '',
        markdown: '',
    })

    const schema = yup.object().shape({
        email: yup.string().email().required(),
        subject: yup.string().min(1).required(),
        markdown: yup.string().min(1).required(),
    })

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    let formIsValid = $derived(schema.isValidSync(form))
    let email_isValid = $derived(validateSyncAt('email', form))
    let subject_isValid = $derived(validateSyncAt('subject', form))
    let markdown_isValid = $derived(validateSyncAt('markdown', form))

    let loadingPreview = $state(false)
    let preview = $state({
        text: '',
        html: '',
    })
    // true: show "Are you sure?"
    let preSend = $state(false)

    let editor: any = $state()
    let editorStartOptsSet = false
    $effect(() => {
        if (!editorStartOptsSet && editor) {
            editorStartOptsSet = true
            editor.setOption('readOnly', preSend)
        }
    })

    unsubs.push(
        user.subscribe(($user) => {
            // @ts-expect-error
            if (form.email == null && $user) form.email = $user.email
        }),
    )

    async function getPreview() {
        const data = { ...form }
        loadingPreview = true

        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/${$userId}/custom-email/preview`, data)
            if (isEqual(data, form)) {
                preview.text = r.data.text
                preview.html = r.data.html
                loadingPreview = false
            } else {
                console.warn('Received markdown preview, but form data has changed, ignoring.')
            }
        } catch {
            if (isEqual(data, form)) {
                loadingPreview = false
            }
        }
    }

    let signinTokenTimer: ReturnType<typeof setTimeout> | undefined = undefined
    function clearPreviewTimer() {
        if (signinTokenTimer != null) {
            clearTimeout(signinTokenTimer)
            signinTokenTimer = undefined
        }
    }

    function delayGetPreview() {
        clearPreviewTimer()
        signinTokenTimer = setTimeout(() => getPreview(), MARKDOWN_UPDATE_DELAY_MS)
    }

    $effect(() => {
        if (form.markdown) delayGetPreview()
    })

    async function send() {
        working = true
        preSend = false

        const data = { ...form }

        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/${$userId}/custom-email/send`, data)
            notify({
                type: 'success',
                title: 'Envoi d’un courriel',
                text: 'Courriel envoyé avec succès.',
            })
            sreplace(`/wdom-user/${$userId}`)
        } catch (e) {
            console.error('Error sending email:', e)
            notify({
                type: 'danger',
                title: 'Envoi d’un courriel',
                text: 'Le courriel n’a pas été envoyé suite à une erreur.',
            })
            working = false
        }
    }
</script>

<Breadcrumb {crumbs} />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else}
    <div class="card custom-email-user-info">
        <div class="card-title text-primary">
            {$_('Adresses')} (langue de l’utilisateur : {#if $loading}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>{:else}{$user.metadata.prefered_lg}{/if})
        </div>

        {#if $loading}
            <div class="card-body">
                <LoadingPattern pattern={['x', 'x', 'x']} />
                <div style="text-align: center; padding: 1em;">Un moment…</div>
                <LoadingPattern pattern={['x', 'x', 'x']} />
            </div>
        {:else}
            <div class="card-body">
                <div class="address-container">
                    {#each $addresses as address}
                        <div class="address-box">
                            <p class="address-title">{address.title}</p>
                            {#if address.comment}
                                <p class="address-comment">{address.comment}</p>
                            {/if}
                            <p>
                                {address.name ? address.name : '<sans nom>'}{#if address.org}, {address.org}{/if}
                                {#if address.email}<br />{address.email}{/if}
                                {#if address.voice}
                                    <br /><InternationalPhone phone={address.voice} />
                                {/if}
                            </p>
                            <p>
                                {#if address.street && address.street.length}
                                    {#each address.street as street, i}
                                        {street}{#if i}<br />{/if}
                                    {/each}
                                {:else}
                                    &lt;sans rue&gt;
                                {/if}
                                {#if address.city || address.sp}
                                    <br />
                                {/if}
                                {#if address.city && address.sp}
                                    {address.city}, <Province contact={address} />
                                {:else if address.city}
                                    {address.city}
                                {:else if address.sp}
                                    <Province contact={address} />
                                {/if}
                                {#if address.country || address.postal_code}
                                    <br />
                                    {#if address.country}<Country contact={address} />{/if}{#if address.country && address.postal_code},
                                    {/if}{#if address.postal_code}<PostalCode contact={address} />{/if}
                                {/if}
                            </p>
                            <div class="fill"></div>
                            {#if address.email}
                                <div class="address-select">
                                    <button type="button" class="btn btn-primary" onclick={() => (form.email = asAny(address.email))} disabled={preSend || form.email === address.email}>Sélectionner</button>
                                </div>
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}
    </div>

    {#if !$loading}
        <div class="row mt-4">
            <div class="col-md-6">
                <form
                    onsubmit={(e) => {
                        e.preventDefault()
                    }}
                    novalidate
                >
                    <div class="card">
                        <div class="card-title text-primary">{$_('Courriel')}</div>
                        <div class="card-body d-grid gap-2">
                            <div class="form-group row">
                                <label class="col-sm-3 col-form-label text-end" for="femail">Destinataire</label>
                                <div class="col-sm-9"><input type="email" id="femail" class="form-control" class:is-invalid={!email_isValid} class:is-valid={email_isValid} bind:value={form.email} disabled={preSend || working} placeholder="abc@domaine.ca" /></div>
                            </div>
                            <div class="form-group row">
                                <label class="col-sm-3 col-form-label text-end" for="fsubject">Sujet</label>
                                <div class="col-sm-9"><input type="text" id="fsubject" class="form-control" class:is-invalid={!subject_isValid} class:is-valid={subject_isValid} bind:value={form.subject} disabled={preSend || working} /></div>
                            </div>
                            <div class="form-group" class:is-invalid={!markdown_isValid}>
                                <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: false, mode: 'markdown', showTrailingSpace: true }} bind:value={form.markdown} bind:editor />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <div class="col-md-6">
                <div class="card">
                    <div class="card-title text-info">Mini antisèche Markdown</div>
                    <div class="card-body">
                        <p>On peut mettre en *italique*, ou **gras**, ou *les **deux***. Paragraphes séparés par une ligne vide. On peut forcer une nouvelle ligne en mettant *deux espaces* à la fin de la ligne.</p>
                        <p>- item 1 d’une liste<br />- item2 d’une liste</p>
                        <p>Lien: [texte du lieu](https://google.ca/)</p>
                        <p>Lien mot de passe: [configurer un mot de passe]({'{pw:link}'}), valide jusqu’au {'{pw:expire}'}</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="row mt-4">
            <div class="col-md-6">
                <div class="card">
                    <div class="card-title text-secondary">
                        Preview : Text {#if loadingPreview}<span class="text-muted">(chargement…)</span>{/if}
                    </div>
                    <div class="card-body">
                        {#if preview.text}
                            <pre>{preview.text}</pre>
                        {:else}
                            <div class="text-muted">(aucune donnée)</div>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="card">
                    <div class="card-title text-secondary">
                        Preview : HTML {#if loadingPreview}<span class="text-muted">(chargement…)</span>{/if}
                    </div>
                    <div class="card-body">
                        {#if preview.html}
                            <div class="well">{@html preview.html}</div>
                        {:else}
                            <div class="text-muted">(aucune donnée)</div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>

        <div class="card mt-4">
            <div class="card-title text-primary">Actions</div>
            <div class="card-body">
                {#if working}
                    <button type="button" class="btn btn-info" disabled
                        ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        Un moment…</button
                    >
                {:else if !preSend}
                    <button type="button" class="btn btn-primary" onclick={() => (preSend = true)} disabled={loadingPreview || !formIsValid}>Envoi</button>
                {:else}
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (preSend = false)}>Annuler</button>
                    <button type="button" class="btn btn-danger" onclick={() => send()} disabled={loadingPreview || !formIsValid}>Confirmer l’envoi</button>
                {/if}
            </div>
        </div>
    {/if}
{/if}
